import faqs from './search-faqs.json';

// Webflow FAQ export (www.viewrail.com FAQs collection, 11 Sep 2026), trimmed to question/answer.
// Matched client-side for now; the intent is to move this to an Algolia index alongside products,
// at which point `matchFaqs` is replaced by a query and the JSON goes away.
export interface SearchFaq {
  question: string;
  answer: string;
  topic: string;
  category: string;
}

const ALL_FAQS: SearchFaq[] = faqs;

// Shown before the shopper types (Figma "Common Questions"): shop-relevant starting points across the
// product lines, rather than the policy questions that dominate the export.
const COMMON_QUESTIONS = [
  'Is Express really something I can install myself?',
  'Should I choose cable or rod infill?',
  'Which glass system is most budget-friendly?',
  'Should I use the shop or the Custom Tread Builder?',
];

export const commonFaqs = COMMON_QUESTIONS.map((q) => ALL_FAQS.find((faq) => faq.question === q)).filter(
  (faq): faq is SearchFaq => !!faq,
);

// Words that appear in most questions and would match everything.
const STOPWORDS = new Set(['the', 'and', 'for', 'can', 'do', 'does', 'how', 'what', 'my', 'is', 'are', 'you', 'viewrail', 'with', 'to', 'of', 'in', 'on', 'it', 'or', 'an']);

const tokenize = (text: string) =>
  text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length >= 2 && !STOPWORDS.has(word));

// Same idea as the backend product search: match on the singular stem, so "treads" finds "tread".
const stem = (term: string) => (term.length > 3 && term.endsWith('s') && !term.endsWith('ss') ? term.slice(0, -1) : term);

// Ranks by where the query words land: a hit in the question counts most, the category/topic label
// next, and the answer body least. FAQs matching more of the query's words come first, so "stair
// tread" leads with the tread questions rather than every question that mentions stairs.
export const matchFaqs = (query: string, limit = 4): SearchFaq[] => {
  const terms = tokenize(query).map(stem);
  if (!terms.length) return [];

  const ranked = ALL_FAQS.map((faq) => {
    const questionWords = tokenize(faq.question);
    const labelWords = tokenize(`${faq.category} ${faq.topic}`);
    const answerWords = tokenize(faq.answer);
    let score = 0;
    let termsMatched = 0;
    for (const term of terms) {
      // Word-prefix matches only, so "led" matches "LEDs" but not "installed".
      const inWords = (words: string[]) => words.some((word) => word.startsWith(term));
      const inQuestion = inWords(questionWords);
      const inLabel = inWords(labelWords);
      // Weights add up, so a question in the matching category ("tread" question filed under
      // Treads) outranks one that only shares the word.
      if (inQuestion) score += 3;
      if (inLabel) score += 1;
      if (inQuestion || inLabel) termsMatched += 1;
      // Answer hits help ordering but not coverage: an answer that mentions "tread" in passing
      // shouldn't make a railing question a "stair tread" result.
      else if (inWords(answerWords)) score += 0.5;
    }
    return { faq, score, termsMatched };
  }).filter(({ termsMatched }) => termsMatched > 0);

  return ranked
    .sort((a, b) => b.termsMatched - a.termsMatched || b.score - a.score)
    .slice(0, limit)
    .map(({ faq }) => faq);
};
